import React from 'react';
import { AlertTriangle, Fingerprint, Sparkles } from 'lucide-react';
import { Switch } from './ui/switch';
import { getImageUrl } from '../lib/api';

export const KLING_MAX_ELEMENTS = 3;

// Mirrors backend pick_element_characters: scene-preferred → speakers (dialogue order) → tagged, image required, max 3.
export const pickKlingIdentities = (scene, dialogueLines = [], characters = []) => {
  if (!scene) return { used: [], dropped: [], noImage: [] };
  const byId = Object.fromEntries(characters.map(c => [c.id, c]));
  const tagged = [];
  for (const key of ['character_ids', 'start_character_ids', 'end_character_ids']) {
    for (const id of scene[key] || []) if (byId[id] && !tagged.includes(id)) tagged.push(id);
  }
  const speakers = (dialogueLines || []).filter(l => l?.text?.trim() && l.character_id).map(l => l.character_id);
  const ordered = [], noImage = [];
  for (const id of [...(scene.native_voice_speakers || []), ...speakers, ...tagged]) {
    const c = byId[id];
    if (!c || ordered.includes(c) || noImage.includes(c)) continue;
    (c.image_storage_path ? ordered : noImage).push(c);
  }
  return { used: ordered.slice(0, KLING_MAX_ELEMENTS), dropped: ordered.slice(KLING_MAX_ELEMENTS), noImage };
};

export const KlingIdentityBadge = ({ character }) => {
  if (!character?.kling_element?.element_id) return null;
  return (
    <span data-testid={`kling-identity-badge-${character.id}`} title="Kling identity locked — this character's face and angles are sent with every Kling render"
      className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/20">
      <Fingerprint className="w-2.5 h-2.5" /> Kling ID
    </span>
  );
};

// Shown under the Kling provider controls: per-project identity-lock switch (off by default) + which characters get anchored.
export const KlingIdentityNotice = ({ scene, dialogueLines, characters, provider, enabled = false, onToggle, saving = false }) => {
  if (provider !== 'kling') return null;
  const { used, dropped, noImage } = pickKlingIdentities(scene, dialogueLines, characters);
  const pending = used.filter(c => !c.kling_element?.element_id);
  return (
    <div data-testid="kling-identity-notice" className="mt-2 space-y-1.5">
      <label className="flex items-center justify-between gap-3 px-2.5 py-2 rounded-lg border border-white/[0.06] bg-white/[0.02] cursor-pointer">
        <span className="flex items-center gap-2 text-[11px] text-[#C9C9CF]">
          <Fingerprint className={`w-3.5 h-3.5 ${enabled ? 'text-[#10B981]' : 'text-[#8A8A93]'}`} />
          <span><b className="font-semibold">Lock character identities</b> <span className="text-[#8A8A93]">· Kling Elements, project-wide · costs Kling units (0.5 per character, once)</span></span>
        </span>
        <Switch data-testid="kling-identity-toggle" checked={enabled} disabled={saving} onCheckedChange={onToggle} className="data-[state=checked]:bg-[#10B981]" />
      </label>
      {!enabled && (
        <p className="text-[10px] text-[#8A8A93] px-1">Off — faces rely on the opening/closing frames only. Turn on if characters drift between shots.</p>
      )}
      {enabled && used.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 text-[10px] text-[#8A8A93]">
          <Fingerprint className="w-3 h-3 text-[#10B981] shrink-0" />
          <span>Identity-locked for Kling:</span>
          {used.map(c => (
            <span key={c.id} data-testid={`kling-identity-chip-${c.id}`} className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border ${c.kling_element?.element_id ? 'bg-[#10B981]/10 text-[#10B981] border-[#10B981]/20' : 'bg-white/[0.04] text-[#C9C9CF] border-white/[0.08]'}`}>
              {c.image_storage_path && <img src={getImageUrl(c.image_storage_path)} alt="" className="w-3.5 h-3.5 rounded-full object-cover" />}
              {c.name}{c.kling_element?.voice_bound ? ' · native voice' : ''}
            </span>
          ))}
        </div>
      )}
      {enabled && pending.length > 0 && (
        <p className="text-[10px] text-[#8A8A93] flex items-start gap-1.5">
          <Sparkles className="w-3 h-3 mt-px shrink-0 text-[#E879F9]" />
          <span>First Kling render builds an identity for <b>{pending.map(c => c.name).join(', ')}</b> (Kling derives extra angles from the reference image, ~1 min and 0.5 Kling units each, once).</span>
        </p>
      )}
      {enabled && (dropped.length > 0 || noImage.length > 0) && (
        <p data-testid="kling-identity-warning" className="text-[10px] text-amber-400 flex items-start gap-1.5">
          <AlertTriangle className="w-3 h-3 mt-px shrink-0" />
          <span>
            {dropped.length > 0 && <>Kling locks max {KLING_MAX_ELEMENTS} identities per clip — <b>{dropped.map(c => c.name).join(', ')}</b> will rely on the frames only. </>}
            {noImage.length > 0 && <><b>{noImage.map(c => c.name).join(', ')}</b> {noImage.length > 1 ? 'have' : 'has'} no reference image, so no identity can be locked.</>}
          </span>
        </p>
      )}
    </div>
  );
};
